import React from 'react'
import styled, { css } from 'styled-components'
import { Icon } from '../../atoms/Icon'
import { IconButton as LibIconButton } from '../../atoms/IconButton'
import { media } from '../../../utils/mediaQuery'

// Container components
export const FilterPanelContainer = styled.div<{
  $collapsed?: boolean
  $width?: number
}>`
  width: ${({ $collapsed, $width }) =>
    $collapsed ? '0' : $width ? `${$width}px` : '280px'};
  background: ${({ theme }) => theme.colors.background.primary};
  border-right: 1px solid ${({ theme }) => theme.colors.border.default};
  position: relative;
  transition: ${({ $width, theme }) =>
    $width ? 'none' : `width ${theme.transitions.default}`};
  overflow-x: hidden;
  overflow-y: visible;
  display: flex;
  flex-direction: column;

  ${media.tabletDown} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-height: calc(100dvh - var(--header-height, 48px));
    height: calc(100dvh - var(--header-height, 48px));
    border-right: none;
    border-top: 1px solid ${({ theme }) => theme.colors.border.default};
    border-radius: ${({ theme }) => theme.borderRadius.large}
      ${({ theme }) => theme.borderRadius.large} 0 0;
    box-shadow: ${({ theme }) => theme.shadows.xl};
    z-index: ${({ theme }) => theme.zIndex.modal};
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: env(safe-area-inset-bottom, 0);
    transform: translateY(${({ $collapsed }) => ($collapsed ? '100%' : '0')});
    ${({ $collapsed }) =>
      $collapsed &&
      css`
        pointer-events: none;
      `}
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  @media print {
    display: none !important;
    visibility: hidden !important;
    width: 0 !important;
  }
`

/** Drag handle visual for mobile bottom sheet */
export const MobileSheetHandle = styled.div`
  display: none;

  ${media.tabletDown} {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0 6px;
    cursor: grab;
    touch-action: none;
    flex-shrink: 0;

    &:active {
      cursor: grabbing;
    }

    &::after {
      content: '';
      width: 36px;
      height: 4px;
      background: ${({ theme }) => theme.colors.border.default};
      border-radius: ${({ theme }) => theme.borderRadius.round};
    }
  }
`

// Header components
export const FilterHeader = styled.div`
  padding-top: ${({ theme }) => theme.spacing.medium};
  padding-bottom: ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  background: ${({ theme }) => theme.colors.background.primary};
`

export const FilterHeaderTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
`

export const FilterTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  margin: 0;
`

export const FilterCount = styled.span`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: 10px;
  font-weight: 600;
  padding: 2px 5px;
  border-radius: 10px;
  min-width: 16px;
  text-align: center;
  display: inline-block;
`

export const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
`

export const FilterActionsSeparator = styled.div`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors.border.default};
  margin: 0 2px;
`

// Using IconButton from library instead of custom implementation
export const IconButton = LibIconButton

// Quick filters components
export const QuickFilters = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.light};
  border-top: 1px solid ${({ theme }) => theme.colors.border.light};
  margin-bottom: ${({ theme }) => theme.spacing.medium};
  margin-top: ${({ theme }) => theme.spacing.medium};
`

export const QuickFiltersTitle = styled.div`
  font-size: 10px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const QuickFiltersActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xsmall};
  min-height: 20px;
`

export const QuickFilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xsmall};
  padding: 3px 8px;
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 3px;
  font-size: 10px;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  white-space: nowrap;

  & > button {
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    width: 10px;
    height: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${({ theme }) => theme.colors.primary};
    transition: color ${({ theme }) => theme.transitions.default};

    &:hover {
      color: ${({ theme }) => theme.colors.text.primary};
    }
  }
`

// Content area
export const FilterContent = styled.div`
  flex: 1;
  overflow-y: auto;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border.default};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.text.secondary};
  }
`

/** Close icon that shows chevron-left on desktop, x on mobile */
const DesktopOnly = styled.span`
  display: inline-flex;
  ${media.tabletDown} {
    display: none;
  }
`
const MobileOnly = styled.span`
  display: none;
  ${media.tabletDown} {
    display: inline-flex;
  }
`
export const FilterCloseIcon: React.FC<{ color?: string }> = ({
  color = '#999',
}) => (
  <>
    <DesktopOnly>
      <Icon name="chevron-left" size="small" color={color} />
    </DesktopOnly>
    <MobileOnly>
      <Icon name="x" size="small" color={color} />
    </MobileOnly>
  </>
)

// Toggle button
export const FilterToggleButton = styled.button<{ $collapsed?: boolean }>`
  position: absolute;
  right: -32px;
  top: 50%;
  transform: translateY(-50%);
  width: 32px;
  height: 64px;
  background: ${({ theme }) => theme.colors.background.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-left: none;
  border-radius: ${({ theme }) =>
    `0 ${theme.borderRadius.medium} ${theme.borderRadius.medium} 0`};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  ${media.tabletDown} {
    display: none;
  }
`

// Filter Panel Component
interface FilterPanelProps {
  title?: string
  filterCount?: number
  collapsed?: boolean
  onToggle?: () => void
  onClear?: () => void
  onSave?: () => void
  quickFilters?: Array<{
    label: string
    onRemove: () => void
  }>
  children: React.ReactNode
  className?: string
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
  title = 'Filters',
  filterCount = 0,
  collapsed = false,
  onToggle,
  onClear,
  onSave,
  quickFilters = [],
  children,
  className,
}) => {
  return (
    <FilterPanelContainer $collapsed={collapsed} className={className}>
      <FilterHeader>
        <FilterHeaderTop>
          <FilterTitle>
            {title}
            {filterCount > 0 && <FilterCount>{filterCount}</FilterCount>}
          </FilterTitle>
          <FilterActions>
            {onSave && (
              <IconButton onClick={onSave} aria-label="Save filters">
                <Icon name="arrow-down" size="small" />
              </IconButton>
            )}
            {onClear && filterCount > 0 && (
              <IconButton onClick={onClear} aria-label="Clear filters">
                <Icon name="refresh" size="small" />
              </IconButton>
            )}
            {onToggle && (
              <IconButton
                onClick={onToggle}
                aria-label="Close filters"
                variant="danger"
              >
                <Icon name="x" size="small" />
              </IconButton>
            )}
          </FilterActions>
        </FilterHeaderTop>

        {quickFilters.length > 0 && (
          <QuickFilters>
            <QuickFiltersTitle>Quick Filters</QuickFiltersTitle>
            <QuickFiltersActions>
              {quickFilters.map((filter, index) => (
                <QuickFilterChip key={index}>
                  {filter.label}
                  <button onClick={filter.onRemove}>
                    <Icon name="x" size="small" />
                  </button>
                </QuickFilterChip>
              ))}
            </QuickFiltersActions>
          </QuickFilters>
        )}
      </FilterHeader>

      <FilterContent>{children}</FilterContent>

      {onToggle && (
        <FilterToggleButton $collapsed={collapsed} onClick={onToggle}>
          <Icon name={collapsed ? 'arrow-right' : 'arrow-left'} size="small" />
        </FilterToggleButton>
      )}
    </FilterPanelContainer>
  )
}
